import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  TableContainer,
  Paper,
  Box,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  CircularProgress,
  IconButton,
  DialogActions,
  TextField,
} from "@mui/material";
import { DialogTitle } from "@mui/material";
import { Checkbox } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  StyledButton,
  StyledCancelButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainerMis,
  StyledAgGridContainer,
  StyledTableContainer,
  StyledTypographyA,
  inputStyle,
  DeleteButton,
} from "components/StyledComponents/styledComponents.js";
import { globalStyles } from "components/Styles/globalStyles";
import URLs from "constants/urls";
import { getPresignedUrl } from "constants/urls";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import { CheckCircle, Visibility, CloudUpload, Download } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import { useMaterialUIController } from "context";

export default function MISReport() {
  const [controller] = useMaterialUIController(); // Get context state
  const { user } = controller; // Destructure user
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [misData, setMisData] = useState([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const MInput = inputStyle();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const jwt_token = Cookie.get("jwt_token");
  const [paramsData, setParamsData] = useState({});
  const [openFileFolder, setOpenFileFolder] = useState(false);
  const [file, setFile] = useState(null);
  const [misDialogOpen, setMisDialogOpen] = useState(false);
  const [misDialogAction, setMisDialogAction] = useState(null);
  const [misDialogMessage, setMisDialogMessage] = useState("");
  const today = dayjs();
  const handleOpenMisDialog = (action, message) => {
    if (selectedEntryIds.length === 0) {
      setAlert("Please select applications", "error");
      return;
    }
    setMisDialogAction(() => action);
    setMisDialogMessage(message);
    setMisDialogOpen(true);
  };
  const handleCloseMisDialog = () => {
    setMisDialogOpen(false);
    setMisDialogAction(null);
    setMisDialogMessage("");
  };
  const handleConfirmMisDialog = () => {
    if (misDialogAction) {
      misDialogAction();
    }
    handleCloseMisDialog();
  };

  const financialYearStart =
    today.month() >= 3
      ? dayjs(`${today.year()}-04-01`).format("YYYY-MM-DD")
      : dayjs(`${today.year() - 1}-04-01`).format("YYYY-MM-DD");
  const currentMonthEnd = today.endOf("month").format("YYYY-MM-DD");

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckboxClick = (entryId) => {
    setSelectedEntryIds((prev) => {
      const updatedIds = prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId];
      const updatedRows = misData.filter((row) => updatedIds.includes(row.entryId));
      setSelectedRows(updatedRows);

      return updatedIds;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getDate()}-${date.toLocaleString("en-US", {
      month: "short",
    })}-${date.getFullYear()}`;
  };

  const handleSelectAll = () => {
    if (selectedEntryIds.length === misData.length) {
      setSelectedEntryIds([]);
      setSelectedRows([]);
    } else {
      setSelectedEntryIds(misData.map((row) => row.entryId));
      setSelectedRows(misData);
    }
  };

  const HeaderCheckbox = () => (
    <>
      <Checkbox
        checked={selectedEntryIds.length === misData.length && misData.length > 0}
        indeterminate={selectedEntryIds.length > 0 && selectedEntryIds.length < misData.length}
        onChange={handleSelectAll}
      />
      <DeleteButton sx={{ fontWeight: "bold", borderRadius: "10px" }}>Select All</DeleteButton>
    </>
  );

  const columns = [
    {
      field: "addToMIS",
      headerName: "Select All",
      headerComponent: HeaderCheckbox, // Use the custom header component
      align: "center",
      width: 150,
      cellRenderer: (params) => {
        if (params.node.rowPinned === "bottom") {
          return null;
        }
        return (
          <>
            <Checkbox
              checked={selectedEntryIds.includes(params.data.entryId)}
              onChange={() => handleCheckboxClick(params.data.entryId)}
            />
            {activeTab === 1 && (
              <>
                <Tooltip title="Upload" arrow>
                  <IconButton onClick={() => handleOpenDialogFileUploader(params)} color="primary">
                    <CloudUpload />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download" arrow>
                  <IconButton onClick={() => triggerS3File(params)} color="primary">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      sortable: true,
      filter: true,
      width: 130,
      valueFormatter: (params) => formatDate(params.value),
      sort: "desc",
    },
    {
      field: "applicationNumber",
      headerName: "Appl. No.",
      sortable: true,
      filter: true,
      flex: 1,
    },
    { field: "customerName", headerName: "Customer Name", sortable: true, filter: true, flex: 1.5 },
    { field: "loanType", headerName: "Business Type", sortable: true, filter: true, flex: 1 },
    { field: "dsaName", headerName: "DSA Name", sortable: true, filter: true },
    { field: "bankName", headerName: "Provider Name", sortable: true, filter: true },
    {
      field: "loanAmount",
      headerName: "Applied Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
    {
      field: "revenuePercentage",
      headerName: "Est Net %",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      field: "revenue",
      headerName: "Actual Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
    {
      field: "dsaPayoutPercentage",
      headerName: "DSA Pay-out (%)",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => (params.value ? `${params.value.toFixed(2)}%` : ""),
    },
    {
      field: "dsaPayoutValue",
      headerName: "DSA Amount",
      sortable: true,
      filter: true,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) || "0.00"}`,
    },
  ];

  const handleOpenDialogFileUploader = (params) => {
    setParamsData(params.data);
    setOpenFileFolder(true);
  };

  const handleCloseDialogFileUploader = () => {
    setOpenFileFolder(false);
    setFile(null);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const triggerS3File = async (params) => {
    setParamsData();
    const url = getPresignedUrl({
      fileName: params.data.applicationNumber,
      group: "mis",
      env: "stage",
      type: "download",
    });
    try {
      const downloadResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (downloadResponse.ok) {
        const presignedUrl = await downloadResponse.text();

        if (!presignedUrl.startsWith("http")) {
          throw new Error("Invalid Pre-Signed URL received");
        }
        if (presignedUrl) {
          window.open(presignedUrl, "_blank");
          return;
        }
      }
    } catch {
      setAlert("Failed to Download", "error");
    }
  };

  const uploadFileToS3 = async () => {
    if (!file) {
      setAlert("Please select a file first.", "error");
      return;
    } else {
      if (!file.name.toLowerCase().endsWith(".eml")) {
        setAlert("Only .eml files are allowed!", "error");
        return;
      }
    }
    // const uploadUrl = `${URLs.fileAttachmentUrl}/${paramsData.applicationNumber}`;
    const uploadUrl = getPresignedUrl({
      fileName: paramsData.applicationNumber,
      group: "mis",
      env: "stage",
      type: "upload",
    });
    setLoading(true);
    try {
      const response = await fetch(uploadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to get pre-signed URL");

      const presignedUrl = await response.text();

      if (!presignedUrl.startsWith("http")) {
        throw new Error("Invalid Pre-Signed URL received");
      }
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "message/rfc822",
        },
      });

      if (uploadResponse.ok) {
        setAlert("File uploaded successfully!", "success");
        setOpenFileFolder(false);
      } else {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.statusText}, Details: ${errorText}`);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      setAlert("File upload failed.", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    let misStatus = activeTab ? "I" : "N";
    fetchMisData(misStatus);
    setSelectedEntryIds([]);
    setSelectedRows([]);
  }, [activeTab]);

  const fetchMisData = async (misStatus) => {
    setLoading(true);
    try {
      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      };

      const apiUrl = `${URLs.dsaMISUrl}?startDate=${financialYearStart}&endDate=${currentMonthEnd}&misStatus=${misStatus}&dsaId=${user.companyId}`;

      console.log("Fetching data from:", apiUrl);

      const response = await fetch(apiUrl, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Fetch error:", response.status, errorText);
        setAlert(`Failed to fetch MIS data: ${response.status}`, "error");
        return;
      }

      const data = await response.json();
      setMisData(data);
    } catch (error) {
      console.error("Error fetching MIS data:", error);
      setAlert("An error occurred while fetching data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMIS = async () => {
    const url = `${URLs.entryMisStateUpdateUrl}`;
    if (selectedEntryIds.length === 0) {
      setAlert("Please select applications", "error");
      return;
    }
    const bodyData = {
      entryIds: [...selectedEntryIds],
      misStatus: "I",
    };

    try {
      setLoading(true);
      const options = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(bodyData),
      };

      const response = await fetch(url, options);
      const responseText = await response.text();

      if (response.ok) {
        setAlert(`${responseText}`, "success");
        const misStatus = activeTab ? "I" : "N";
        fetchMisData(misStatus);
      } else {
        setAlert(`Failed to update data: ${responseText}`, "error");
      }
    } catch (error) {
      setAlert("An error occurred while updating data", "error");
    } finally {
      setLoading(false);
    }
  };

  const onSelectionChanged = useCallback((params) => {
    const selectedData = params.api.getSelectedRows();
    setSelectedRows(selectedData);
    setSelectedEntryIds(selectedData.map((row) => row.entryId));
  }, []);

  const downloadAndSubmitSelectedRows = async () => {
    if (selectedEntryIds.length === 0) {
      setAlert("Please select at least one row to download.", "error");
      return;
    }
    const bodyData = {
      entryIds: [...selectedEntryIds],
      misStatus: "C",
    };

    try {
      setLoading(true);
      const options = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(bodyData),
      };
      const url = `${URLs.entryMisStateUpdateUrl}`;

      const response = await fetch(url, options);
      const responseText = await response.text();

      if (response.ok) {
        setAlert(`${responseText}`, "success");
        const misStatus = activeTab ? "I" : "N";
        fetchMisData(misStatus);
      } else {
        setAlert(`Failed to update data: ${responseText}`, "error");
      }
    } catch (error) {
      setAlert("An error occurred while updating data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [{ label: "Entries" }, { label: "Approve MIS" }];

  return (
    <DashboardLayout>
      <Dialog
        open={loading}
        onClose={null}
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none", // Optional: Remove any shadow for a clean look
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent", // Ensure the content background is transparent too
            boxShadow: "none", // Optional: Remove content shadow for clean design
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ marginTop: 2, color: "white" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <Dialog open={openFileFolder} onClose={handleCloseDialogFileUploader}>
        <StyledTypographyA mt={2}>Upload File</StyledTypographyA>
        <DialogContent>
          <TextField
            sx={MInput.b}
            InputLabelProps={{
              sx: {
                color: MInput.a,
              },
            }}
            type="file"
            onChange={handleFileChange}
          />
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseDialogFileUploader} color="secondary">
            Cancel
          </StyledButton>
          <StyledButton onClick={uploadFileToS3} color="primary" variant="contained">
            Submit
          </StyledButton>
        </DialogActions>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <StyledTableContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            overflow: "hidden",
            flexGrow: 1,
            boxShadow: "none",
            border: "none",
            backgroundColor: "transparent",
          }}
        >
          <ResponsiveTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={handleChange}
            labelColor="#ffffff"
            bgColor="#e0f7fa"
          />
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              mt: 0,
              p: 0,
              pt: 2,
              pr: 0,
              backgroundColor: "transparent", // Transparent background
              boxShadow: "none", // Remove shadow if any
              border: "none", // Remove border
            }}
          >
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  backgroundColor: "transparent", // Transparent background
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <StyledButton
                    onClick={() =>
                      handleOpenMisDialog(handleAddToMIS, "Are you sure you want to add to MIS?")
                    }
                    disabled={loading}
                  >
                    Add to MIS
                  </StyledButton>
                </Box>

                <StyledAgGridContainer
                  component={Paper}
                  className="ag-theme-alpine"
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "transparent", // Ensure grid is transparent
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <AgGridReact
                    rowData={misData}
                    columnDefs={columns}
                    pagination={true}
                    paginationPageSize={11}
                  />
                </StyledAgGridContainer>
              </Box>
            )}

            {activeTab === 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  backgroundColor: "transparent", // Transparent background
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <StyledButton
                    onClick={() =>
                      handleOpenMisDialog(
                        downloadAndSubmitSelectedRows,
                        "Are you sure you want to approve?"
                      )
                    }
                    disabled={loading}
                  >
                    Approve
                  </StyledButton>
                </Box>
                <StyledAgGridContainer
                  component={Paper}
                  className="ag-theme-alpine"
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  <AgGridReact
                    rowData={misData}
                    columnDefs={columns}
                    pagination={true}
                    paginationPageSize={11}
                  />
                </StyledAgGridContainer>
              </Box>
            )}
            <Dialog open={misDialogOpen} onClose={handleCloseMisDialog}>
              <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
                {misDialogMessage || "Are you sure you want to proceed with this action?"}
              </DialogContent>
              <DialogActions>
                <StyledCancelButton onClick={handleCloseMisDialog} color="secondary">
                  Cancel
                </StyledCancelButton>
                <StyledButton onClick={handleConfirmMisDialog} color="primary">
                  Confirm
                </StyledButton>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </StyledTableContainer>
    </DashboardLayout>
  );
}
